import React, { useContext, useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import deepEqual from 'react-fast-compare'
import { Container } from 'typescript-ioc/es5'
import { NotificationListContext } from './notification-list-context'
import { REFRESH_OPTIONS } from './constants'
import { NotificationSource } from '../../enums/notification-source'
import { HOLD_OUT_COMPUTED_STATUS, StatusType } from '../../enums/status-type'
import { ReloadOutlined, GlobalOutlined, ApartmentOutlined } from '@ant-design/icons'
import { NotificationListState } from './interfaces'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import { NotificationListTypes } from './enums'
import { SegmentDto } from '../../dtos/segment'
import { FilterBar, Select, StatusBadge } from '@pushly/aqe/lib/components'
import type { FilterDefinition, ActiveFilter } from '@pushly/aqe/lib/components'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { DomainService } from '../../services'
import { SegmentSource } from '@pushly/models/lib/enums'
import { Button, Flex, Input, Switch, Tooltip, Typography } from 'antd'
import { getSegmentFilterOptions } from '../../_utils/segment-list'

type FilterKey = keyof NotificationListState['filters']

interface INotificationListHeaderProps {
    title?: string
    filterSize?: SizeType
    filtersAddonBefore?: React.ReactNode
    filtersAddonAfter?: React.ReactNode
    hideRefresh?: boolean
    hideAutoRefreshOptions?: boolean
    hideFilters?: FilterKey | FilterKey[] | boolean
    filtersDisabled?: FilterKey | FilterKey[] | boolean
    listType?: NotificationListTypes
    segments?: SegmentDto[]
    userId?: number
    domainId?: number
    orgId?: number
}

const NotificationListHeader = (props: INotificationListHeaderProps) => {
    const {
        initialLoadComplete,
        levelDependenciesLoaded,
        dataSourceLoaded,
        refreshing,
        filters,
        onSearchClick,
        onFilterChange,
        onRefreshClick,
        refreshEnabled,
        refreshTimeout,
        onRefreshEnabledChange,
        onRefreshTimeoutChange,
        ...ctx
    } = useContext(NotificationListContext)

    const isDomainLevel = ctx.level === 'domain'
    const levelResourceId = isDomainLevel ? props.domainId : props.orgId
    const [prevLevelResourceId, setPrevLevelResourceId] = useState(levelResourceId)
    const userPreferences = useUserPreferences(
        props.userId,
        isDomainLevel ? levelResourceId : undefined,
        !isDomainLevel ? levelResourceId : undefined,
    )

    let liveTypeTimeout: any = null

    const [status, setStatus] = useState<string[]>(userPreferences.get(['notification_status_filters'], []))
    const [source, setSource] = useState<string[]>(userPreferences.get(['notification_source_filters'], []))
    const [segments, setSegments] = useState<{ value: number; label: string }[]>(
        userPreferences.get(['notification_segment_filters'], []).map((obj: any) => {
            const [[key, label]] = Object.entries(obj)
            return { value: Number(key), label }
        }),
    )

    useEffect(() => {
        if (prevLevelResourceId !== levelResourceId) {
            if (filters.status && !deepEqual(status, filters.status)) {
                setStatus(filters.status)
            }

            if (filters.source && !deepEqual(source, filters.source)) {
                setSource(filters.source)
            }

            if (filters.segments && !deepEqual(segments, filters.segments)) {
                setSegments(filters.segments)
            }

            setPrevLevelResourceId(levelResourceId)
        }
    }, [levelResourceId, prevLevelResourceId])

    const allFilters: FilterKey[] = ['source', 'status', 'search', 'showAllNotifications']
    const filterSize = props.filterSize ?? 'middle'
    const { listType } = props

    let filtersHidden: FilterKey[] = []
    if (props.hideFilters === true) {
        filtersHidden = Array.from(allFilters)
    } else if (props.hideFilters) {
        filtersHidden = Array.isArray(props.hideFilters) ? props.hideFilters : [props.hideFilters]
    }

    let filtersDisabled: FilterKey[] = []
    if (props.filtersDisabled === true) {
        filtersDisabled = Array.from(allFilters)
    } else if (props.filtersDisabled) {
        filtersDisabled = Array.isArray(props.filtersDisabled) ? props.filtersDisabled : [props.filtersDisabled]
    }

    const hideAllFilters = filtersHidden.length === allFilters.length
    const hideAllRefresh = props.hideRefresh && props.hideAutoRefreshOptions

    const loading = !initialLoadComplete && !levelDependenciesLoaded && !dataSourceLoaded

    const statusOptions = [
        {
            value: StatusType.COMPLETED.name,
            label: <StatusBadge type="notification" status={StatusType.COMPLETED.name} expanded hideTooltip />,
        },
        {
            value: StatusType.SCHEDULED.name,
            label: <StatusBadge type="notification" status={StatusType.SCHEDULED.name} expanded hideTooltip />,
        },
        {
            value: StatusType.DELIVERING.name,
            label: <StatusBadge type="notification" status={StatusType.DELIVERING.name} expanded hideTooltip />,
        },
        {
            value: HOLD_OUT_COMPUTED_STATUS,
            label: <StatusBadge type="notification" status={StatusType.COMPLETED.name} holdOut expanded hideTooltip />,
        },
        {
            value: StatusType.CANCELLED.name,
            label: <StatusBadge type="notification" status={StatusType.CANCELLED.name} expanded hideTooltip />,
        },
        {
            value: StatusType.FAILED.name,
            label: <StatusBadge type="notification" status={StatusType.FAILED.name} expanded hideTooltip />,
        },
    ]

    const sourceOptions = [
        { value: NotificationSource.MANUAL, label: 'Manual' },
        { value: NotificationSource.FEED, label: 'Feed' },
    ]

    const isOrgLevel = ctx.level === 'org'

    const filterDefinitions: FilterDefinition[] = useMemo(() => {
        const domainId = props.domainId
        const defs: FilterDefinition[] = [
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: statusOptions,
                condition: () => !filtersHidden.includes('status') && listType === NotificationListTypes.NOTIFICATION,
            },
            {
                key: 'source',
                label: 'Source',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: sourceOptions,
                condition: () => !filtersHidden.includes('source') && listType === NotificationListTypes.NOTIFICATION,
            },
            {
                key: 'segments',
                label: 'Segments',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: true,
                loadOptions: async () => {
                    if (!domainId) return []
                    const domainService = Container.get(DomainService)
                    const res = await domainService.fetchSegmentsByDomainId(domainId, {
                        query: {
                            includeTreated: 0,
                            pagination: 0,
                            fields: 'id,name,source,computedStatus,isDefault,iconUrl,flags,lastTargetedDateUtc,segmentType',
                            source: SegmentSource.STANDARD,
                        },
                    })
                    if (!res.ok) return []
                    return getSegmentFilterOptions(res.data)
                },
                condition: () => !filtersHidden.includes('segments'),
            },
            {
                key: 'scope',
                label: 'Scope',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: 'multi-domain', label: 'Multi-Domain', icon: <ApartmentOutlined />, disabled: true },
                    { value: 'single-domain', label: 'Single-Domain', icon: <GlobalOutlined /> },
                ],
                condition: () => !filtersHidden.includes('showAllNotifications') && isOrgLevel,
            },
        ]
        return defs
    }, [filtersHidden, listType, props.domainId, isOrgLevel])

    const getFilterBarValue = (): ActiveFilter[] => {
        const activeFilters: ActiveFilter[] = []
        if (status.length > 0) {
            activeFilters.push({ key: 'status', operator: 'in', value: status })
        }
        if (source.length > 0) {
            activeFilters.push({ key: 'source', operator: 'in', value: source })
        }
        if (segments.length > 0) {
            activeFilters.push({ key: 'segments', operator: 'in', value: segments.map((s) => s.value) })
        }
        // Scope filter: multi-domain is always selected, single-domain is optional
        if (isOrgLevel) {
            const scopeValue = filters.showAllNotifications ? ['multi-domain', 'single-domain'] : ['multi-domain']
            activeFilters.push({ key: 'scope', operator: 'in', value: scopeValue })
        }
        return activeFilters
    }

    const handleFilterBarApply = (appliedFilters: ActiveFilter[]) => {
        const statusFilter = appliedFilters.find((f) => f.key === 'status')
        const newStatus = (statusFilter?.value as string[]) || []
        if (!deepEqual(newStatus, status)) {
            setStatus(newStatus)
            onFilterChange('status', newStatus)
        }

        const sourceFilter = appliedFilters.find((f) => f.key === 'source')
        const newSource = (sourceFilter?.value as string[]) || []
        if (!deepEqual(newSource, source)) {
            setSource(newSource)
            onFilterChange('source', newSource)
        }

        const segmentsFilter = appliedFilters.find((f) => f.key === 'segments')
        const newSegmentIds = (segmentsFilter?.value as number[]) || []
        // Reconstruct { value, label } objects from the segment IDs
        // Use props.segments (loaded by parent) to resolve labels
        const allSegments = props.segments ?? []
        const newSegments = newSegmentIds.map((id) => {
            const seg = allSegments.find((s) => s.id === id)
            return { value: id, label: seg?.name ?? String(id) }
        })
        if (!deepEqual(newSegments, segments)) {
            setSegments(newSegments)
            onFilterChange('segments', newSegments)
        }

        // Handle scope filter - single-domain presence means showAllNotifications is true
        const scopeFilter = appliedFilters.find((f) => f.key === 'scope')
        const scopeValues = (scopeFilter?.value as string[]) || ['multi-domain']
        const showSingleDomain = scopeValues.includes('single-domain')
        if (showSingleDomain !== filters.showAllNotifications) {
            onFilterChange('showAllNotifications', showSingleDomain)
        }
    }

    return (
        <Flex className={classnames('notification-list-header')} gap={8} justify="space-between">
            <Flex flex={1} align="center" gap={8}>
                <div
                    className={classnames('notification-list-title', {
                        hidden: !props.title,
                    })}
                >
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        {props.title}
                    </Typography.Title>
                </div>

                <Flex
                    className={classnames('notification-list-filters', {
                        hidden: hideAllFilters,
                    })}
                    gap={8}
                    flex={1}
                >
                    {props.filtersAddonBefore}

                    {!filtersHidden.includes('search') && (
                        <Input.Search
                            className={classnames('notification-list-search')}
                            style={{ width: 300 }}
                            size={filterSize}
                            placeholder="Search by ID or Notification Content"
                            disabled={filtersDisabled.includes('search')}
                            enterButton={true}
                            defaultValue={filters.search}
                            onSearch={onSearchClick}
                            onChange={(ev) => {
                                clearTimeout(liveTypeTimeout)

                                const value = ev.target.value
                                liveTypeTimeout = setTimeout(() => {
                                    onFilterChange('search', value)
                                }, 320)
                            }}
                        />
                    )}

                    <FilterBar
                        filterDefinitions={filterDefinitions}
                        value={getFilterBarValue()}
                        onApply={handleFilterBarApply}
                        showInlineTags={false}
                        showInlineClearAll={false}
                        disabled={loading || filtersDisabled.includes('status')}
                        size={filterSize as 'small' | 'middle' | 'large'}
                    />

                    {props.filtersAddonAfter}
                </Flex>
            </Flex>
            <div
                className={classnames('notification-list-refresh', {
                    hidden: hideAllRefresh,
                })}
                style={{
                    borderLeft: hideAllFilters ? 'none' : '1px solid var(--aqe-color-border-default)',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                {!props.hideAutoRefreshOptions && (
                    <>
                        <Switch
                            size="small"
                            checked={refreshEnabled}
                            onChange={onRefreshEnabledChange}
                            aria-label="Enable Auto-Refresh"
                        />
                        <span>Auto-Refresh</span>

                        <Select
                            className={classnames('notification-list-timeout-select')}
                            aria-label="Auto-refresh interval"
                            size="small"
                            disabled={!levelDependenciesLoaded || !refreshEnabled}
                            options={REFRESH_OPTIONS}
                            value={refreshTimeout}
                            onChange={onRefreshTimeoutChange}
                        />
                    </>
                )}

                {!props.hideRefresh && (
                    <Tooltip title="Refresh">
                        <Button
                            className={classnames('notification-list-refresh-toggle')}
                            aria-label="Refresh notifications"
                            type="text"
                            shape="round"
                            icon={<ReloadOutlined className="text--size--large" spin={loading || refreshing} />}
                            onClick={onRefreshClick}
                            disabled={!levelDependenciesLoaded || !refreshEnabled}
                        />
                    </Tooltip>
                )}
            </div>
        </Flex>
    )
}

export default NotificationListHeader
